/**
 * gamePlayService — client wrapper around the submit-game-play edge function
 * and the leaderboard RPCs (get_game_leaderboard / get_global_leaderboard).
 *
 * Centralises analytics + error handling so DynamicGameRenderer stays UI-only.
 */

import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics/ga";
import type { GameRunResult } from "@/types/gameSpec";

export interface SubmittedSession {
  session_id: string;
  score: number;
  max_score: number;
  won: boolean;
  spec_snapshot_hash: string;
}

export interface LeaderboardRow {
  user_id: string;
  display_name: string;
  best_score: number;
  plays: number;
  last_played: string;
}

export interface GlobalLeaderboardRow {
  user_id: string;
  display_name: string;
  total_score: number;
  games_played: number;
  last_played: string;
}

export const gamePlayService = {
  async submitRun(
    gameId: string,
    result: GameRunResult,
    opts?: { specSnapshotHash?: string; sharedFromUserId?: string | null },
  ): Promise<SubmittedSession> {
    trackEvent("game_play_submit_started", { game_id: gameId });

    const { data, error } = await supabase.functions.invoke("submit-game-play", {
      body: {
        game_id: gameId,
        score: result.score,
        scene_count: result.scene_count,
        scenes_correct: result.scenes_correct,
        streak_max: result.streak_max,
        time_spent_ms: result.time_spent_ms,
        attempts: result.attempts,
        spec_snapshot_hash: opts?.specSnapshotHash,
        shared_from_user_id: opts?.sharedFromUserId ?? null,
      },
    });

    if (error) {
      trackEvent("game_play_submit_failed", {
        game_id: gameId,
        error: error.message,
      });
      throw error;
    }

    trackEvent("game_play_submit_succeeded", {
      game_id: gameId,
      score: data.score,
      won: data.won,
    });
    return data as SubmittedSession;
  },

  async getGameLeaderboard(
    gameId: string,
    limit = 20,
  ): Promise<LeaderboardRow[]> {
    const { data, error } = await supabase.rpc("get_game_leaderboard", {
      _game_id: gameId,
      _limit: limit,
    });
    if (error) throw error;
    return (data ?? []) as LeaderboardRow[];
  },

  async getGlobalLeaderboard(limit = 20): Promise<GlobalLeaderboardRow[]> {
    const { data, error } = await supabase.rpc("get_global_leaderboard", {
      _limit: limit,
    });
    if (error) throw error;
    return (data ?? []) as GlobalLeaderboardRow[];
  },
};
